import React from 'react'
import { Link } from 'react-router-dom'
import {
  HomeIcon,
  MagnifyingGlassIcon,
  InformationCircleIcon,
  UserPlusIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  LifebuoyIcon,
  ChatBubbleLeftRightIcon,
  MapIcon
} from '@heroicons/react/24/outline'
import SEO from '@components/common/SEO'

interface SitemapSection {
  title: string
  icon: React.ElementType
  links: {
    path: string
    label: string
    description?: string
  }[]
}

const SitemapPage: React.FC = () => {
  const lastUpdated = '1 ตุลาคม 2025'

  const sitemapSections: SitemapSection[] = [
    {
      title: 'หน้าหลัก',
      icon: HomeIcon,
      links: [
        {
          path: '/',
          label: 'หน้าแรก',
          description: 'ค้นหาบริษัทรักษาความปลอดภัยทั่วไทย'
        },
        {
          path: '/search',
          label: 'ค้นหาบริษัท',
          description: 'ค้นหาและกรองบริษัทตามเงื่อนไข'
        }
      ]
    },
    {
      title: 'เกี่ยวกับเรา',
      icon: InformationCircleIcon,
      links: [
        {
          path: '/about',
          label: 'เกี่ยวกับเรา',
          description: 'ทำความรู้จักกับไทยเซ็กคิวริตี้ไดเรกทอรี่'
        },
        {
          path: '/contact',
          label: 'ติดต่อเรา',
          description: 'ช่องทางติดต่อและที่ตั้งสำนักงาน'
        }
      ]
    },
    {
      title: 'ช่วยเหลือและคำถาม',
      icon: LifebuoyIcon,
      links: [
        {
          path: '/help',
          label: 'ศูนย์ช่วยเหลือ',
          description: 'คู่มือและวิธีการใช้งานเว็บไซต์'
        },
        {
          path: '/faq',
          label: 'คำถามที่พบบ่อย',
          description: 'คำตอบสำหรับคำถามยอดนิยม'
        }
      ]
    },
    {
      title: 'ผู้ใช้งาน',
      icon: UserCircleIcon,
      links: [
        {
          path: '/register',
          label: 'สมัครสมาชิก',
          description: 'สร้างบัญชีฟรีเพื่อใช้งานเต็มรูปแบบ'
        },
        {
          path: '/login',
          label: 'เข้าสู่ระบบ',
          description: 'เข้าสู่ระบบสำหรับสมาชิก'
        },
        {
          path: '/profile',
          label: 'โปรไฟล์',
          description: 'จัดการข้อมูลส่วนตัว (สมาชิกเท่านั้น)'
        },
        {
          path: '/my-claims',
          label: 'My Claims',
          description: 'จัดการบริษัทที่ Claim ไว้ (สมาชิกเท่านั้น)'
        }
      ]
    },
    {
      title: 'นโยบายและข้อกำหนด',
      icon: ShieldCheckIcon,
      links: [
        {
          path: '/privacy',
          label: 'นโยบายความเป็นส่วนตัว',
          description: 'การเก็บรักษาและใช้งานข้อมูลส่วนบุคคล'
        },
        {
          path: '/terms',
          label: 'ข้อกำหนดการใช้งาน',
          description: 'เงื่อนไขและข้อตกลงการใช้บริการ'
        }
      ]
    },
    {
      title: 'ติดต่อและรายงาน',
      icon: ChatBubbleLeftRightIcon,
      links: [
        {
          path: '/report',
          label: 'รายงานปัญหา',
          description: 'แจ้งปัญหาหรือข้อมูลผิดพลาด'
        },
        {
          path: '/feedback',
          label: 'แนะนำและติชม',
          description: 'แชร์ความคิดเห็นและข้อเสนอแนะ'
        }
      ]
    },
    {
      title: 'อื่นๆ',
      icon: MapIcon,
      links: [
        {
          path: '/sitemap',
          label: 'แผนผังเว็บไซต์',
          description: 'รายการหน้าทั้งหมดในเว็บไซต์'
        }
      ]
    }
  ]

  const sitemapStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "แผนผังเว็บไซต์ - ไทยเซ็กคิวริตี้ไดเรกทอรี่",
    "description": "แผนผังเว็บไซต์และรายการหน้าทั้งหมดของไทยเซ็กคิวริตี้ไดเรกทอรี่",
    "dateModified": "2025-10-01"
  }

  return (
    <>
      <SEO
        title="แผนผังเว็บไซต์"
        description="แผนผังเว็บไซต์และรายการหน้าทั้งหมดของไทยเซ็กคิวริตี้ไดเรกทอรี่ - ค้นหาและเข้าถึงทุกส่วนของเว็บไซต์ได้อย่างง่ายดาย"
        keywords="sitemap, แผนผังเว็บไซต์, รายการหน้า, navigation"
      />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(sitemapStructuredData) }}
      />

      <div className="bg-gradient-to-b from-primary-50 to-white min-h-screen">
        {/* Hero Section */}
        <div className="bg-primary-600 text-white py-16">
          <div className="container-wrapper">
            <div className="max-w-3xl mx-auto text-center">
              <div className="flex justify-center mb-6">
                <MapIcon className="w-16 h-16" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                แผนผังเว็บไซต์
              </h1>
              <p className="text-xl text-primary-100">
                รายการหน้าทั้งหมดในเว็บไซต์ เพื่อการเข้าถึงที่ง่ายดาย
              </p>
              <p className="text-sm text-primary-200 mt-4">
                อัปเดตล่าสุด: {lastUpdated}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container-wrapper py-12">
          <div className="max-w-6xl mx-auto">
            {/* Introduction */}
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 mb-8">
              <p className="text-neutral-700 leading-relaxed">
                แผนผังเว็บไซต์นี้แสดงรายการหน้าทั้งหมดในไทยเซ็กคิวริตี้ไดเรกทอรี่
                ช่วยให้คุณค้นหาและเข้าถึงเนื้อหาที่ต้องการได้อย่างรวดเร็ว
              </p>
            </div>

            {/* Sitemap Sections */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {sitemapSections.map((section, sectionIndex) => {
                const Icon = section.icon

                return (
                  <div
                    key={sectionIndex}
                    className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 hover:shadow-md transition-shadow duration-200"
                  >
                    {/* Section Header */}
                    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-neutral-200">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-primary-600" />
                      </div>
                      <h2 className="text-xl font-bold text-neutral-900">
                        {section.title}
                      </h2>
                    </div>

                    {/* Links */}
                    <ul className="space-y-3">
                      {section.links.map((link, linkIndex) => (
                        <li key={linkIndex}>
                          <Link
                            to={link.path}
                            className="group block p-3 rounded-lg hover:bg-primary-50 transition-colors duration-200"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <h3 className="font-semibold text-neutral-900 group-hover:text-primary-600 transition-colors">
                                  {link.label}
                                </h3>
                                {link.description && (
                                  <p className="text-sm text-neutral-600 mt-1">
                                    {link.description}
                                  </p>
                                )}
                              </div>
                              <span className="text-neutral-400 group-hover:text-primary-600 transition-colors flex-shrink-0">
                                →
                              </span>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              })}
            </div>

            {/* Statistics */}
            <div className="mt-12 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg shadow-lg p-8 text-white">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold mb-2">
                    {sitemapSections.reduce((total, section) => total + section.links.length, 0)}
                  </div>
                  <div className="text-primary-100 text-sm">
                    หน้าทั้งหมด
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-bold mb-2">
                    {sitemapSections.length}
                  </div>
                  <div className="text-primary-100 text-sm">
                    หมวดหมู่หลัก
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-bold mb-2">
                    100%
                  </div>
                  <div className="text-primary-100 text-sm">
                    ครอบคลุมทุกหน้า
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Navigation */}
            <div className="mt-12 bg-blue-50 rounded-lg border border-blue-200 p-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">
                🚀 เริ่มต้นใช้งานอย่างรวดเร็ว
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Link
                  to="/"
                  className="bg-white rounded-lg p-4 text-center hover:shadow-md transition-shadow border border-blue-100"
                >
                  <HomeIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <span className="text-sm font-semibold text-blue-900">หน้าแรก</span>
                </Link>
                <Link
                  to="/search"
                  className="bg-white rounded-lg p-4 text-center hover:shadow-md transition-shadow border border-blue-100"
                >
                  <MagnifyingGlassIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <span className="text-sm font-semibold text-blue-900">ค้นหาบริษัท</span>
                </Link>
                <Link
                  to="/register"
                  className="bg-white rounded-lg p-4 text-center hover:shadow-md transition-shadow border border-blue-100"
                >
                  <UserPlusIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <span className="text-sm font-semibold text-blue-900">สมัครสมาชิก</span>
                </Link>
                <Link
                  to="/help"
                  className="bg-white rounded-lg p-4 text-center hover:shadow-md transition-shadow border border-blue-100"
                >
                  <LifebuoyIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <span className="text-sm font-semibold text-blue-900">ศูนย์ช่วยเหลือ</span>
                </Link>
              </div>
            </div>

            {/* SEO Note */}
            <div className="mt-8 bg-neutral-50 border border-neutral-200 rounded-lg p-6">
              <h3 className="font-semibold text-neutral-900 mb-2">
                📌 หมายเหตุสำหรับการค้นหา (SEO)
              </h3>
              <p className="text-sm text-neutral-700 leading-relaxed">
                หน้านี้เป็น HTML Sitemap สำหรับผู้ใช้งาน สำหรับเครื่องมือค้นหา (Search Engines)
                เรามี XML Sitemap แยกต่างหากที่ <code className="bg-neutral-200 px-2 py-1 rounded text-xs">/sitemap.xml</code>
                ซึ่งจะอัปเดทอัตโนมัติเมื่อมีการเพิ่มหน้าหรือบริษัทใหม่
              </p>
            </div>

            {/* Footer CTA */}
            <div className="mt-12 text-center">
              <p className="text-neutral-600 mb-4">
                ไม่พบหน้าที่ต้องการ? ติดต่อเราได้เลย
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors duration-200 shadow-md"
                >
                  ติดต่อเรา
                </Link>
                <Link
                  to="/feedback"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-primary-600 font-semibold rounded-lg hover:bg-primary-50 transition-colors duration-200 border-2 border-primary-600"
                >
                  แนะนำเว็บไซต์
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default SitemapPage
